/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import useIPMSApi from "./ipms";

interface FileAttachment {
  attachment_id: number;
  file_name: string;
  file_extension: string;
  file_size: number;
  uploaded_at: string;
  description?: string;
  uploaded_by?: string;
}

interface AssessmentFileViewerProps {
  reviewId: number;
  employeeId: number;
  enableDownload?: boolean;
}

const AssessmentFileViewer: React.FC<AssessmentFileViewerProps> = ({
  reviewId,
  employeeId,
  enableDownload = true,
}) => {
  const { fetchAttachments, downloadAssessmentAttachment } = useIPMSApi();

  const [files, setFiles] = useState<FileAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<number | null>(null);

  useEffect(() => {
    if (reviewId) {
      loadAttachments();
    }
  }, [reviewId]);

  const loadAttachments = async () => {
    try {
      setLoading(true);
      console.log(
        "[AssessmentFileViewer] Loading attachments for review:",
        reviewId,
      );

      const res = await fetchAttachments(reviewId);

      if (res?.status && res?.data?.attachments) {
        setFiles(res.data.attachments);
      } else if (res?.data?.attachments) {
        setFiles(res.data.attachments);
      } else {
        setFiles([]);
      }
    } catch (error) {
      console.error("[AssessmentFileViewer] Error loading attachments:", error);
      toast.error("Error loading attachments");
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (attachmentId: number, fileName: string) => {
    if (!enableDownload) return;

    try {
      setDownloading(attachmentId);
      console.log(
        "[AssessmentFileViewer] Downloading attachment:",
        attachmentId,
      );

      // Fetch the response (blob or data) from hook
      const response = await downloadAssessmentAttachment(
        attachmentId,
        fileName,
      );

      if (response) {
        // Handle Blob download directly in browser
        const blob =
          response instanceof Blob
            ? response
            : new Blob([response.data || response]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();

        // Cleanup DOM element and object URL
        link.remove();
        window.URL.revokeObjectURL(url);

        toast.success(`Downloaded: ${fileName}`);
      } else {
        toast.error("Download failed");
      }
    } catch (error) {
      console.error("[AssessmentFileViewer] Error downloading file:", error);
      toast.error("Error downloading file");
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center">
        <p className="text-gray-600 dark:text-gray-400">Loading files...</p>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="p-4 text-center">
        <p className="text-gray-600 dark:text-gray-400 italic">
          No files uploaded
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {files.map((file) => (
        <div
          key={file.attachment_id}
          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
        >
          <div className="flex-1">
            <p className="font-medium text-gray-900 dark:text-white">
              {file.file_name}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {file.file_size
                ? `${(file.file_size / 1024).toFixed(2)} KB`
                : "Unknown size"}{" "}
              • {new Date(file.uploaded_at).toLocaleDateString()}
            </p>
            {file.description && (
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                {file.description}
              </p>
            )}
          </div>
          {enableDownload && (
            <button
              onClick={() => handleDownload(file.attachment_id, file.file_name)}
              disabled={downloading === file.attachment_id}
              className="ml-4 p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Download file"
            >
              {downloading === file.attachment_id ? (
                <span className="text-sm">Downloading...</span>
              ) : (
                <DocumentArrowDownIcon className="w-5 h-5" />
              )}
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default AssessmentFileViewer;
